/**
 * Per-role dashboard layout descriptors.
 *
 * Each entry describes the welcome banner's 3 KPIs and an array of rows,
 * each row containing up to 3 tiles by 12-col span.
 *
 * DashboardPage reads `layouts[dashboardRole]`, falls back to `layouts.default`
 * if the role has no layout yet (useful during phased rollout).
 *
 * To add a tile: register the component in tileRegistry (DashboardPage.js)
 * then reference its key + props here.
 */

const DEFAULT_WELCOME_KPIS = ['tasks_assigned', 'placeholder', 'placeholder'];
const DRIVER_WELCOME_KPIS = ['tasks_assigned', 'space_calls_queue', 'avg_resolution'];

const layouts = {
  // ────────────────────────────────────────────────────────────────
  // CEO / Admin / Super Admin — Phase 1a
  // §2 MyWork (span 8) + §3 Sales Leaderboard (span 4)
  // §4 Driver Leaderboard (span 8) + §5 Expenses This Month (span 4)
  // ────────────────────────────────────────────────────────────────
  ceo: {
    welcomeKpis: DEFAULT_WELCOME_KPIS,
    rows: [
      {
        cols: [
          { span: 8, tile: 'MyWork' },
          {
            span: 4,
            tile: 'LeaderboardMini',
            props: {
              // Real group names in the DB are short keys ('sales' / 'driver'
              // etc.) — same convention the sidebar uses. Display title is
              // still the human-readable "Sales Leaderboard" for the card.
              groupName: 'sales',
              title: 'Sales Leaderboard',
            },
          },
        ],
      },
      {
        cols: [
          {
            span: 8,
            tile: 'LeaderboardMini',
            props: {
              // Aggregate sub-team boards into one company-wide view.
              // Every driver is enrolled in exactly one sub-team (dallas/
              // denton/fort_worth/day) — there is no parent 'driver'
              // group with all 17 drivers in it, so fetching each and
              // merging client-side is the correct view. KPIs with the
              // same name get summed; rankings re-rank across all groups.
              groupNames: ['dallas', 'denton', 'fort_worth', 'day'],
              title: 'Driver Leaderboard',
              showFooterTotals: true,
              footerTowGroups: ['dallas', 'denton', 'fort_worth', 'day'],
            },
          },
          {
            span: 4,
            tile: 'StatCard',
            props: {
              source: 'expenses_month_total',
              title: 'Expenses This Month',
              href: '/sales/expenses',
              accent: 'gold',
            },
          },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // Sales — Phase 1c
  // Per Matt's spec §2/§3 are SWAPPED from other roles — leaderboard
  // first, My Work second. §4 is two stacked KPI blocks:
  //   TOP:    No-Charges / Refunds / Discounts this month (per user —
  //           /api/requests/stats is already visibility-scoped).
  //   BOTTOM: Leaderboard KPIs per user (New Accounts, Tows, Cold
  //           Contacts, Accounts Visited, Accounts Lost YTD — all
  //           come from /api/leaderboard/:groupName).
  // §5 Stipend Usage.
  // ────────────────────────────────────────────────────────────────
  sales: {
    welcomeKpis: DEFAULT_WELCOME_KPIS,
    rows: [
      {
        cols: [
          {
            span: 8,
            tile: 'LeaderboardMini',
            props: {
              // Short key — matches the real group_name in DB (same as
              // sidebar TEAM_TO_GROUP mapping).
              groupName: 'sales',
              title: 'Sales Leaderboard',
            },
          },
          { span: 4, tile: 'MyWork' },
        ],
      },
      {
        // §4 TOP — Requests buckets
        cols: [
          { span: 8, tile: 'RequestsBuckets', props: { title: 'My Requests This Month' } },
          { span: 4, tile: 'StipendUsage' },
        ],
      },
      {
        // §4 BOTTOM — all Sales KPI totals from the sales leaderboard
        cols: [
          {
            span: 12,
            tile: 'LeaderboardMini',
            props: {
              groupName: 'sales',
              title: 'My KPI Totals',
            },
          },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // Driver — Phase 1b
  // Per Matt's spec:
  //   §2 Driver Leaderboard — their assigned board
  //   §3 Dispatch Queue (total / claimed / avg resolution)
  //   §4 TDLR + Driver's License snapshot with expirations
  //   §5 Weekly Tows Personal Count
  //   §6 Welcome banner KPIs: Tasks Assigned, Space Calls in Queue,
  //      Avg Resolution (not the default placeholder pair).
  // ────────────────────────────────────────────────────────────────
  driver: {
    welcomeKpis: DRIVER_WELCOME_KPIS,
    rows: [
      {
        cols: [
          {
            span: 8,
            tile: 'LeaderboardMini',
            props: {
              // Sentinel '$sub_team' → resolves to the driver's sub-team
              // slug at render time (Dallas Driver → 'dallas'). Top-level
              // Driver Operations users fall back to 'driver' parent board.
              groupName: '$sub_team',
              title: 'Driver Leaderboard',
            },
          },
          { span: 4, tile: 'DispatchQueue' },
        ],
      },
      {
        cols: [
          {
            span: 8,
            tile: 'ComplianceExpiration',
            props: {
              categories: ['TDLR', 'Driver', 'License'],
            },
          },
          {
            span: 4,
            tile: 'StatCard',
            props: {
              title: 'Weekly Tows',
              source: 'driver_weekly_tows',
              accent: 'gold',
            },
          },
        ],
      },
      // Damage Fund Balance tile removed until the driver-facing rollout
      // (fund is admin-only for now — restore the StatCard row here then).
      {
        cols: [
          {
            // One-shift opt-in for drivers working a night off. Shows
            // schedule state; off-shift drivers get a pickup button.
            span: 4,
            tile: 'ShiftPickup',
          },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // Apollo GM — Phase 1d
  // Per Matt's spec:
  //   §2 My Work
  //   §3 Sales Admin Tasks  (mini task board scoped to Sales Admin pos)
  //   §4 Truck Maintenance Tasks (scoped to Truck Maintenance position)
  //   §5 Open Sign Requests + Open Tagging Requests (side by side)
  // ────────────────────────────────────────────────────────────────
  apollo_gm: {
    welcomeKpis: DEFAULT_WELCOME_KPIS,
    rows: [
      {
        cols: [
          { span: 8, tile: 'MyWork' },
          {
            span: 4,
            tile: 'PositionWorkflowBucket',
            props: { positionName: 'Sales Admin', title: 'Sales Admin Tasks' },
          },
        ],
      },
      {
        cols: [
          {
            span: 12,
            tile: 'PositionWorkflowBucket',
            props: { positionName: 'Truck Maintenance', title: 'Truck Maintenance Tasks' },
          },
        ],
      },
      {
        // The two field-work request tiles sit side by side — Tagging reads
        // right next to Sign Requests (Matt, Aug 30).
        cols: [
          { span: 6, tile: 'SignRequestsPlaceholder' },
          { span: 6, tile: 'TaggingRequestsPlaceholder' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // Papa's Lot GM — Phase 1d
  // Per Matt's spec: §2 My Work, §3 Dispatch Section (scoped to
  // Dispatcher position). §4 and §5 blank — collapse them.
  // ────────────────────────────────────────────────────────────────
  papas_lot_gm: {
    welcomeKpis: DEFAULT_WELCOME_KPIS,
    rows: [
      {
        cols: [
          { span: 8, tile: 'MyWork' },
          {
            span: 4,
            tile: 'PositionWorkflowBucket',
            props: { positionName: 'Dispatcher', title: 'Dispatch Tasks' },
          },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // Blank-template roles — just My Work (span 12) + welcome banner.
  // Per spec: Truck Maintenance, Sales Admin, Sign Technician, and
  // both Dispatcher flavors (FW + Denton) get nothing beyond §2.
  // Explicit entries rather than relying on the 'default' fallback —
  // clearer intent if/when these roles get expanded later.
  // ────────────────────────────────────────────────────────────────
  truck_maintenance: { welcomeKpis: DEFAULT_WELCOME_KPIS, rows: [{ cols: [{ span: 12, tile: 'MyWork' }] }] },
  sales_admin:       { welcomeKpis: DEFAULT_WELCOME_KPIS, rows: [{ cols: [{ span: 12, tile: 'MyWork' }] }] },
  sign_technician:   { welcomeKpis: DEFAULT_WELCOME_KPIS, rows: [{ cols: [{ span: 12, tile: 'MyWork' }] }] },
  dispatcher:        { welcomeKpis: DEFAULT_WELCOME_KPIS, rows: [{ cols: [{ span: 12, tile: 'MyWork' }] }] },

  // ────────────────────────────────────────────────────────────────
  // Default — fallback for any role not explicitly listed.
  // Same as the blank-template roles: MyWork + welcome banner.
  // ────────────────────────────────────────────────────────────────
  default: {
    welcomeKpis: DEFAULT_WELCOME_KPIS,
    rows: [
      { cols: [{ span: 12, tile: 'MyWork' }] },
    ],
  },
};

export default layouts;
